import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import { AutoPayDTO, PaymentData, PaymentsState } from "./payments-state";
import { defaultGenericMeta } from "store/common";
import {
  fetchAutoPay,
  saveAutoPayInfo,
  submitAndPay,
  deleteAutoPayInfo,
} from "./payments-action";
import { PaymentActivity } from "store/paymentActivity/paymentActivity-state";
import { RootState } from "store";

const paymentsEntityAdapter = createEntityAdapter<AutoPayDTO>({});

export const paymentSlice = createSlice({
  name: "autopay",
  initialState: paymentsEntityAdapter.getInitialState<PaymentsState>({
    meta: { ...defaultGenericMeta },
  }),
  reducers: {
    setPaymentData(state, action: PayloadAction<PaymentData>) {
      state.paymentData = action.payload;
    },
    resetInfo(state) {
      delete state.paymentData;
      state.meta = { ...defaultGenericMeta };
    },
  },
  extraReducers: (builder) => {
    /* fetch auto pay info */
    builder.addCase(
      fetchAutoPay.fulfilled,
      (state, { payload }: PayloadAction<AutoPayDTO[]>) => {
        state.meta.isLoading = false;
        state.meta.isSuccess = true;
        state.meta.status = "idle";
        paymentsEntityAdapter.setAll(state, payload);
      }
    );
    builder.addCase(fetchAutoPay.pending, (state) => {
      state.meta.isLoading = true;
      state.meta.status = "inProcess";
    });
    builder.addCase(fetchAutoPay.rejected, (state) => {
      state.meta.isLoading = false;
      state.meta.status = "idle";
      state.meta.error = "true";
    });
    /* create and update autopay */
    builder.addCase(
      saveAutoPayInfo.fulfilled,
      (state, { payload }: PayloadAction<AutoPayDTO>) => {
        state.meta.isLoading = false;
        state.meta.isSuccess = true;
        state.meta.status = "idle";
        paymentsEntityAdapter.upsertOne(state, payload);
      }
    );
    builder.addCase(saveAutoPayInfo.pending, (state) => {
      state.meta.isLoading = true;
      state.meta.status = "inProcess";
    });
    builder.addCase(saveAutoPayInfo.rejected, (state) => {
      state.meta.isLoading = false;
      state.meta.status = "idle";
      state.meta.error = "true";
    });
    /* Delete autopay */
    builder.addCase(
      deleteAutoPayInfo.fulfilled,
      (state, { payload: id }: PayloadAction<string>) => {
        state.meta.isLoading = false;
        state.meta.isSuccess = true;
        state.meta.status = "idle";
        paymentsEntityAdapter.removeOne(state, id);
      }
    );
    builder.addCase(deleteAutoPayInfo.pending, (state) => {
      state.meta.isLoading = true;
      state.meta.status = "inProcess";
    });
    builder.addCase(deleteAutoPayInfo.rejected, (state) => {
      state.meta.isLoading = false;
      state.meta.status = "idle";
      state.meta.error = "true";
    });
    /* submitAndPay */
    builder.addCase(
      submitAndPay.fulfilled,
      (state, { payload }: PayloadAction<PaymentActivity>) => {
        state.meta.isLoading = false;
        state.meta.isSuccess = true;
        state.meta.status = "idle";
        if (state.paymentData) {
          state.paymentData.paymentResponse = payload;
        }
      }
    );
    builder.addCase(submitAndPay.pending, (state) => {
      state.meta.isLoading = true;
      state.meta.status = "inProcess";
    });
    builder.addCase(submitAndPay.rejected, (state) => {
      state.meta.isLoading = false;
      state.meta.status = "idle";
      state.meta.error = "true";
    });
  },
});

export const { selectAll: selectAutoPay } = paymentsEntityAdapter.getSelectors(
  (state: RootState) => state.payments
);

export const { setPaymentData, resetInfo } = paymentSlice.actions;
export default paymentSlice.reducer;
